// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PlanCard from "@/components/PlanCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Subscription() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [selectedDevice, setSelectedDevice] = useState("");
  const [loading, setLoading] = useState(false);
  const [bundle, setBundle] = useState(null);

  const load = useCallback(async () => {
    await Promise.all([
      api.get("/plans").then(r => setPlans(r.data)),
      api.get("/subscriptions").then(r => setSubs(r.data)),
      api.get("/devices").then(r => setDevices(r.data)),
      api.get("/subscriptions/bundle-preview").then(r => setBundle(r.data)).catch(() => {}),
    ]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (planId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan_id: planId,
        cycle,
        device_id: selectedDevice || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (sid) => {
    if (!window.confirm("Cancel this subscription?")) return;
    await api.post(`/subscriptions/${sid}/cancel`);
    toast.success("Subscription canceled");
    load();
  };

  const activeSub = subs.find(s => s.status === "active");

  return (
    <div className="space-y-10">
      <div>
        <div className="overline text-secondary">Membership</div>
        <h1 className="mt-1 font-display text-4xl font-light tracking-tight">Your subscription</h1>
        <p className="mt-2 text-muted-foreground">Manage your Treasurescare plan and billing cycle.</p>
      </div>

      {activeSub && (
        <div className="bg-primary text-primary-foreground rounded-2xl p-8 shadow-teal-glow relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-40" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="overline text-secondary">Active plan</div>
              <div className="mt-2 font-display text-4xl capitalize">{activeSub.plan_id} · {activeSub.cycle}</div>
              <div className="mt-2 text-sm text-primary-foreground/70">Membership {activeSub.membership_id} · £{activeSub.amount_gbp.toFixed(2)}</div>
              <div className="mt-1 text-xs text-primary-foreground/60">
                Renews {activeSub.renews_at ? new Date(activeSub.renews_at).toLocaleDateString() : "—"}
              </div>
            </div>
            <Button variant="outline" onClick={() => cancel(activeSub.id)} className="border-secondary text-secondary hover:bg-secondary hover:text-teal">
              Cancel subscription
            </Button>
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="overline text-secondary">{activeSub ? "Change or upgrade" : "Get protected"}</div>
            <h2 className="mt-1 font-display text-3xl">Pick a plan</h2>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {devices.length > 0 && (
              <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                <SelectTrigger className="w-56"><SelectValue placeholder="Cover a device (optional)" /></SelectTrigger>
                <SelectContent>
                  {devices.map(d => <SelectItem key={d.id} value={d.id}>{d.brand} {d.model}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="inline-flex bg-muted rounded-full p-1">
              {["monthly", "quarterly", "annual"].map(c => (
                <button
                  key={c}
                  onClick={() => setCycle(c)}
                  data-testid={`cycle-${c}`}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${cycle === c ? "bg-primary text-primary-foreground" : "text-foreground/70"}`}
                >
                  {c[0].toUpperCase() + c.slice(1)}
                  {c === "quarterly" && <span className="text-[9px] text-secondary font-semibold">−5%</span>}
                  {c === "annual" && <span className="text-[9px] text-secondary font-semibold">SAVE 15%</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        {cycle === "annual" && (
          <div data-testid="annual-savings-banner" className="mt-4 inline-flex items-center gap-2 rounded-full bg-secondary/10 border border-secondary/30 px-3 py-1.5 text-xs text-secondary">
            <span className="font-semibold">Save 15%</span>
            <span className="text-secondary/80">— equivalent to 2 months free every year.</span>
          </div>
        )}
        {bundle && (
          <div
            data-testid="bundle-banner"
            className={`mt-4 rounded-2xl p-4 flex items-center gap-3 ${
              bundle.qualifies
                ? "bg-primary/5 border border-primary/30 text-primary"
                : "bg-muted/40 border border-border text-foreground/70"
            }`}
          >
            <span className="text-2xl">{bundle.qualifies ? "🎉" : "👨‍👩‍👦"}</span>
            <div className="flex-1 text-sm">
              <div className="font-medium">{bundle.qualifies ? "Family bundle unlocked" : "Family bundle"}</div>
              <div className="text-xs mt-0.5 opacity-80">{bundle.message}</div>
            </div>
            {bundle.qualifies && (
              <span className="text-[10px] overline px-2 py-1 rounded-full bg-primary text-primary-foreground font-semibold" data-testid="bundle-discount-chip">
                −{bundle.discount_percent}%
              </span>
            )}
          </div>
        )}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map(p => (
            <PlanCard key={p.id} plan={p} cycle={cycle} onSubscribe={subscribe} loading={loading} ctaLabel={activeSub ? "Switch" : "Subscribe"} />
          ))}
        </div>
      </div>
    </div>
  );
}
